import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateQuotationInput,
  CustomerQuotations,
  Quotation,
  QuotationDownload,
  QuotationPage,
  ReviseQuotationInput,
  UpdateQuotationInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  acceptQuotation,
  createQuotation,
  discardQuotation,
  getCustomerQuotations,
  getQuotationDownload,
  rejectQuotation,
  reviseQuotation,
  searchQuotations,
  sendQuotation,
  sendQuotationOnWhatsapp,
  updateQuotation,
  type QuotationSearchParams,
} from './quotations-api.js';

/**
 * The quotation hooks.
 *
 * None of these goes through the offline write queue, and the reasoning is the invoice's rather
 * than the payment's: sending draws a number from a per-year series and puts a price in front of
 * a customer. Replaying that from a queue twenty minutes later, against shop settings that may
 * have changed in between, is not something anybody wants happening quietly. A quotation that
 * fails to send because the connection dropped is one the shop sends again, deliberately.
 */

export const quotationKeys = {
  all: ['quotations'] as const,
  customer: (customerId: string) => ['quotations', 'customer', customerId] as const,
  search: (params: QuotationSearchParams) => ['quotations', 'search', params] as const,
};

/**
 * Everything the shop has quoted. `placeholderData` keeps the previous page on screen while the
 * next one loads, so growing the list or switching a filter does not blank it under somebody's
 * hand.
 *
 * `enabled` exists for the one caller that must not ask at all: a tailor is refused every
 * quotation route, and a screen that renders its refusal while still firing a request that 403s
 * is teaching the API to log an error about something nobody did wrong.
 */
export function useQuotationSearch(
  params: QuotationSearchParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<QuotationPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: quotationKeys.search(params),
    queryFn: () => searchQuotations(client, params),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  });
}

/** Every quotation given to one customer. See `getCustomerQuotations`. */
export function useCustomerQuotations(
  customerId: string | null,
): UseQueryResult<CustomerQuotations> {
  const client = useApiClient();
  return useQuery({
    queryKey: quotationKeys.customer(customerId ?? ''),
    queryFn: () => getCustomerQuotations(client, customerId as string),
    enabled: customerId !== null,
  });
}

/** Invalidate what a written quotation can have changed. */
function useQuotationInvalidation(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: quotationKeys.all });
  };
}

export function useCreateQuotation(): UseMutationResult<Quotation, unknown, CreateQuotationInput> {
  const client = useApiClient();
  const invalidate = useQuotationInvalidation();
  return useMutation({
    mutationFn: (input: CreateQuotationInput) => createQuotation(client, input),
    onSuccess: invalidate,
  });
}

export function useUpdateQuotation(): UseMutationResult<
  Quotation,
  unknown,
  { quotationId: string; patch: UpdateQuotationInput }
> {
  const client = useApiClient();
  const invalidate = useQuotationInvalidation();
  return useMutation({
    mutationFn: ({ quotationId, patch }: { quotationId: string; patch: UpdateQuotationInput }) =>
      updateQuotation(client, quotationId, patch),
    onSuccess: invalidate,
  });
}

export function useDiscardQuotation(): UseMutationResult<void, unknown, string> {
  const client = useApiClient();
  const invalidate = useQuotationInvalidation();
  return useMutation({
    mutationFn: (quotationId: string) => discardQuotation(client, quotationId),
    onSuccess: invalidate,
  });
}

export function useSendQuotation(): UseMutationResult<Quotation, unknown, string> {
  const client = useApiClient();
  const invalidate = useQuotationInvalidation();
  return useMutation({
    mutationFn: (quotationId: string) => sendQuotation(client, quotationId),
    onSuccess: invalidate,
  });
}

export function useReviseQuotation(): UseMutationResult<
  Quotation,
  unknown,
  { quotationId: string; input?: ReviseQuotationInput }
> {
  const client = useApiClient();
  const invalidate = useQuotationInvalidation();
  return useMutation({
    mutationFn: ({ quotationId, input }: { quotationId: string; input?: ReviseQuotationInput }) =>
      reviseQuotation(client, quotationId, input ?? {}),
    onSuccess: invalidate,
  });
}

export function useAcceptQuotation(): UseMutationResult<Quotation, unknown, string> {
  const client = useApiClient();
  const invalidate = useQuotationInvalidation();
  return useMutation({
    mutationFn: (quotationId: string) => acceptQuotation(client, quotationId),
    onSuccess: invalidate,
  });
}

export function useRejectQuotation(): UseMutationResult<
  Quotation,
  unknown,
  { quotationId: string; reason?: string | null }
> {
  const client = useApiClient();
  const invalidate = useQuotationInvalidation();
  return useMutation({
    mutationFn: ({ quotationId, reason }: { quotationId: string; reason?: string | null }) =>
      rejectQuotation(client, quotationId, { reason: reason ?? null }),
    onSuccess: invalidate,
  });
}

/**
 * Ask for a link and open it.
 *
 * A mutation rather than a query, exactly as the invoice's is: the link is short-lived and
 * rendering the PDF has a cost, so it is fetched when somebody presses a button and never on
 * render. `refresh` is left off by default — a sent quotation is frozen, so unlike a bill there
 * is nothing about it that can have gone stale since it was rendered.
 */
export function useQuotationDownload(): UseMutationResult<
  QuotationDownload,
  unknown,
  { quotationId: string; refresh?: boolean }
> {
  const client = useApiClient();
  return useMutation({
    mutationFn: ({ quotationId, refresh }: { quotationId: string; refresh?: boolean }) =>
      getQuotationDownload(client, quotationId, { ...(refresh ? { refresh } : {}) }),
  });
}

export function useSendQuotationOnWhatsapp(): UseMutationResult<Quotation, unknown, string> {
  const client = useApiClient();
  const invalidate = useQuotationInvalidation();
  return useMutation({
    mutationFn: (quotationId: string) => sendQuotationOnWhatsapp(client, quotationId),
    onSuccess: invalidate,
  });
}
